import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsAPI, json } from 'client/data/api/api-client';
import { get } from 'lodash-es';
import { InventoryModel } from 'client/data/models/inventory';
import { HTTP_ACCEPTED } from 'client/utils/http-status';
import { withRetry } from 'client/utils/promise';
import { PageModel } from 'client/data/models/page';
import { getDealerRooftopId } from 'site-modules/shared/utils/dealer';
import { getAppliedRadiusForSemanticSearch } from 'site-modules/shared/utils/inventory/srp-utils';
import { logger } from 'client/utils/logger/logger';

import { VisitorModel } from './visitor';

export const LLM_TIMEOUT = 10000;
export const LLM_GET_RETRIES_COUNT = 2;

function parseJSON(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

export function buildSearchIdPath(route) {
  return `routes["${route || 'srp_search'}"].searchId`;
}

const withRetry202Response = (promiseCall, retries, delayMS) => {
  // Rejects promise when 202 response received to let withRetry helper trigger retry.
  const reject202Response = response => {
    if (response.status === HTTP_ACCEPTED) {
      return Promise.reject({});
    }

    return response;
  };

  return withRetry((...params) => promiseCall(...params).then(reject202Response), retries, delayMS);
};

export const SemanticSearchModel = createModelSegment('semanticSearch', [
  {
    path: 'userSearch',
  },
  /**
   * @see buildSearchIdPath
   */
  {
    path: 'routes["{route}"].searchId',
    async resolve({ route }, context) {
      const [userSearch, location, visitorLocation, selectedFacets, inventoryData] = await Promise.all([
        context.resolveValue('userSearch'),
        context.resolveValue('location', PageModel, false),
        context.resolveValue('location', VisitorModel, false),
        context.resolveValue('searchResultsFilter', InventoryModel, false),
        context.resolveValue('searchResults', InventoryModel, false),
      ]);

      if (!userSearch) return '';

      const radius = getAppliedRadiusForSemanticSearch({ inventoryData, visitorLocation, selectedFacets });
      const dealerId = getDealerRooftopId({ location });
      const { inventoryType, parentDealership, roi, targetGroups } = selectedFacets ?? {};
      const zip = visitorLocation?.zipCode;

      const request = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userSearch,
          extra: {
            facets: {
              zip,
              radius,
              dealerId,
              inventoryType,
              parentDealership,
              roi: roi?.[0],
              targetGroups,
            },
          },
        }),
        timeout: LLM_TIMEOUT,
      };

      try {
        const searchData = await EdmundsAPI.fetchJson(`/llm/v1/ask/${route}/fallback-source-0/`, request);
        return get(searchData, 'id', '');
      } catch {
        return null;
      }
    },
  },
  {
    path: 'suggestedFilters["{searchId}"]',
    async resolve({ searchId }, context) {
      try {
        const fetchWithoutBuiltinRetries = async (resourcePath, options) =>
          // We pass retries: 0 here for disabling default retry logic, provided by api-client, because of explicit usage of withRetry below
          EdmundsAPI.fetch(resourcePath, { ...options, retries: 0 });

        const fetchWithCustomRetry = withRetry202Response(fetchWithoutBuiltinRetries, LLM_GET_RETRIES_COUNT);
        const searchDataResponse = await fetchWithCustomRetry(`/llm/v1/get/${searchId}`, { timeout: LLM_TIMEOUT });
        const searchData = await json(searchDataResponse);
        const parsedDelta = parseJSON(get(searchData, 'delta'));
        const modifiedZip = get(searchData, 'struct.modifiedFacets.zip');

        if (modifiedZip) {
          try {
            await context.updateValue('location', { zipCode: modifiedZip }, VisitorModel);
          } catch (e) {
            logger.error(e);
          }
        }

        return {
          facetsByName: get(searchData, 'struct.facetsByName', []),
          inventoryCount: get(searchData, 'struct.inventoryCount', 0),
          modifiedRadius: get(searchData, 'struct.modifiedFacets.radius'),
          modifiedZip,
          identifiedVin: get(searchData, 'struct.identifiedVin'),
          deltaRadius: parsedDelta?.radius,
        };
      } catch {
        return null;
      }
    },
  },
]);
